// Sales invoice import draft API (phase 1).
//
// Exact backend contract in backend/src/routes/invoices.js (`/import-drafts`
// subpaths, mounted under /api/invoices): every endpoint requires invoice
// import management (fail-closed Emil/Tomek allowlist). Draft endpoints never
// approve and never settle — no approval call exists here because no approval
// route exists in this phase. Payloads forward caller input verbatim; this
// module invents no document numbers, totals or FX.
import api from '../../../services/api';
import { normalizeSalesInvoiceDraft } from '../../../dto/workflowInvoice';
import { normalizeOrderAdvanceBalance, normalizeOrderSalesInvoices } from '../../../dto/orderSalesInvoice';
import type { AdvanceInvoiceBalance } from './invoiceWorkflowApi';
import type { OrderSalesInvoice } from '../../../types/orderSalesInvoice';
import type { WorkflowInvoice } from '../../../types/workflowInvoice';
import type {
  ApplyExtractionInput,
  ExtractionRequestResult,
} from './purchaseInvoiceImportApi';

export type SalesInvoiceDraft = WorkflowInvoice;

export interface SalesInvoiceDraftSearch {
  status?: string;
  orderId?: string;
  documentNumber?: string;
  fromDate?: string;
  toDate?: string;
  limit?: number;
  offset?: number;
}

export async function createSalesInvoiceDraft(
  input: Record<string, unknown>,
): Promise<SalesInvoiceDraft> {
  const response = await api.post('/invoices/import-drafts', input);
  return normalizeSalesInvoiceDraft(response.data, 'salesInvoiceDraft.create');
}

export async function searchSalesInvoiceDrafts(
  query: SalesInvoiceDraftSearch = {},
): Promise<SalesInvoiceDraft[]> {
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params.append(key === 'documentNumber' ? 'number' : key, String(value));
    }
  });
  const response = await api.get(`/invoices/import-drafts${params.toString() ? `?${params.toString()}` : ''}`);
  return (Array.isArray(response.data) ? response.data : []).map((item: unknown) =>
    normalizeSalesInvoiceDraft(item, 'salesInvoiceDraft.search'),
  );
}

export async function fetchSalesInvoiceDraft(id: string): Promise<SalesInvoiceDraft> {
  const response = await api.get(`/invoices/import-drafts/${id}`);
  return normalizeSalesInvoiceDraft(response.data, 'salesInvoiceDraft.detail');
}

export async function updateSalesInvoiceDraft(
  id: string,
  input: Record<string, unknown>,
  expectedVersion: number,
): Promise<SalesInvoiceDraft> {
  const response = await api.put(`/invoices/import-drafts/${id}`, { ...input, expectedVersion });
  return normalizeSalesInvoiceDraft(response.data, 'salesInvoiceDraft.update');
}

export async function deleteSalesInvoiceDraft(id: string): Promise<void> {
  await api.delete(`/invoices/import-drafts/${id}`);
}

// „Faktury sprzedaży” of a ZK card (sales document readers; not an import endpoint).
export async function fetchOrderSalesInvoices(orderId: string): Promise<{ invoices: OrderSalesInvoice[]; advanceBalance: AdvanceInvoiceBalance | null }> {
  const response = await api.get(`/invoices/order/${orderId}/documents`);
  return { invoices: normalizeOrderSalesInvoices(response.data), advanceBalance: normalizeOrderAdvanceBalance(response.data) };
}

export async function attachSalesInvoiceSourceFile(
  id: string,
  file: File,
): Promise<SalesInvoiceDraft> {
  const payload = new FormData();
  payload.append('file', file);
  const response = await api.post(`/invoices/import-drafts/${id}/files`, payload, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return normalizeSalesInvoiceDraft(response.data, 'salesInvoiceDraft.attachFile');
}

export async function downloadSalesInvoiceSourceFile(id: string, sha256?: string): Promise<Blob> {
  const response = await api.get(`/invoices/import-drafts/${id}/file`, {
    params: sha256 ? { sha256 } : undefined,
    responseType: 'blob',
  });
  return response.data;
}

export async function requestSalesInvoiceExtraction(
  id: string,
  files: File[],
): Promise<ExtractionRequestResult> {
  const payload = new FormData();
  files.forEach((file) => payload.append('files', file));
  const response = await api.post(`/invoices/import-drafts/${id}/extract`, payload, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
}

export async function applySalesInvoiceExtraction(
  id: string,
  input: ApplyExtractionInput,
): Promise<SalesInvoiceDraft> {
  const response = await api.post(`/invoices/import-drafts/${id}/apply-extraction`, input);
  return normalizeSalesInvoiceDraft(response.data, 'salesInvoiceDraft.applyExtraction');
}
